import { useCallback, useEffect, useMemo } from "react";
import { Observable } from "core/Misc/observable";
import { useSettingsStore } from "../contexts/settingsContext";
import { useObservableState } from "./observableHooks";
/**
 * Hook that reads and writes a setting from the settings store.
 * @param descriptor The setting descriptor that identifies the setting and its default value.
 * @returns A tuple of [currentValue, setValue, resetValue] similar to React's useState.
 */
export function useSetting(descriptor) {
    const settingsStore = useSettingsStore();
    // Only watch for this specific setting to change. Otherwise, any time any setting changes we would
    // call readSetting again, which if it is an object, it will be a new instance, which can cause
    // unnecessary re-renders in consumers of this hook.
    const settingObservable = useMemo(() => new Observable(), []);
    useEffect(() => {
        if (settingsStore) {
            const observer = settingsStore.onChanged.add((key) => {
                if (key === descriptor.key) {
                    settingObservable.notifyObservers();
                }
            });
            return () => {
                observer.remove();
            };
        }
        return undefined;
    }, [settingsStore, descriptor.key]);
    const value = useObservableState(useCallback(() => settingsStore?.readSetting(descriptor) ?? descriptor.defaultValue, [settingsStore, descriptor.key, descriptor.defaultValue]), settingObservable);
    const setValue = useCallback((newValue) => {
        const value = typeof newValue === "function" ? newValue(settingsStore?.readSetting(descriptor) ?? descriptor.defaultValue) : newValue;
        settingsStore?.writeSetting(descriptor, value);
    }, [settingsStore, descriptor.key, descriptor.defaultValue]);
    const resetValue = useCallback(() => settingsStore?.writeSetting(descriptor, descriptor.defaultValue), [settingsStore, descriptor.key, descriptor.defaultValue]);
    if (!settingsStore) {
        throw new Error("Settings store is not available in context.");
    }
    return [value, setValue, resetValue];
}
//# sourceMappingURL=settingsHooks.js.map